import { ArrowClockwiseIcon } from '@phosphor-icons/react';
import { Breadcrumb } from 'components/Breadcrumb';
import { PageHeader } from 'components/PageHeader';
import { ITabItem, Tabs } from 'components/tabs/Tabs';
import { CustomerDocumentType } from 'src/types/enums';
import { InternalRole } from '../../../auth/endpoints/MeEndpoint.types';
import { useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useParams, useResolvedPath } from 'react-router-dom';
import { invariant } from '@utils/invariant';
import * as Yup from 'yup';

import { Button } from '../../../../components/button/Button';
import { DataField } from '../../../../components/DataField';
import { ConfirmDialog } from '../../../../components/dialog/ConfirmDialog';
import { InputDialog } from '../../../../components/dialog/InputDialog';
import { getDisplayError } from '../../../../utils/get-display-error';
import { FileUploaderButton } from '../../../document/components/FileUploaderButton';
import { MultiFileUploaderButton } from '../../../document/components/MultiFileUploaderButton';
import { CustomerProvider } from './customerContext';
import { useAuth } from '../../../../contexts/auth-context';
import { PageHeading } from 'components/PageHeading';
import { ErrorState } from 'components/error-state/ErrorState';
import { DelayedSpinner } from 'components/Spinner';
import { captureException } from '@sentry/react';
import { roleToNumber } from 'src/app/auth/roles.client';
import {
  type Customer as CustomerModel,
  linkCustomerDocument,
  unlinkCustomerDocument,
  updateCustomer as updateCustomerFetcher,
  useCustomerById,
} from '../../models';

export type Customer = NonNullable<CustomerModel>;

const CustomerPage = () => {
  const { me } = useAuth();
  const { customerId } = useParams<{ customerId: string }>();
  invariant(customerId);
  const { data, error, isLoading, mutate: refetch } = useCustomerById(+customerId);

  const generalRoute = useResolvedPath('general');
  const contactsRoute = useResolvedPath('contacts');
  const usersRoute = useResolvedPath('users');
  const sustainabilityRoute = useResolvedPath('sustainability');
  const apiKeysRoute = useResolvedPath('api-keys');
  const auditLogsRoute = useResolvedPath('audit-logs');
  const tabItems = useMemo<ITabItem[]>(() => {
    const res = [
      {
        title: 'Algemeen',
        path: generalRoute.pathname,
      },
      {
        title: 'Contacten',
        path: contactsRoute.pathname,
      },
      {
        title: 'Gebruikers klantenportaal',
        path: usersRoute.pathname,
      },
      {
        title: 'Sustainability',
        path: sustainabilityRoute.pathname,
      },
      {
        title: 'API Keys',
        path: apiKeysRoute.pathname,
      },
    ];

    if (roleToNumber(me.role) >= roleToNumber(InternalRole.InternalAdmin)) {
      res.push({
        title: 'Audit logs',
        path: auditLogsRoute.pathname,
      });
    }

    return res;
  }, []);

  const refreshData = useCallback(() => {
    return refetch();
  }, [refetch]);

  const customer = data;

  if (isLoading) {
    return <DelayedSpinner size={8} />;
  }

  if (!customer) {
    return (
      <ErrorState
        error={error}
        title={error ? 'Klant kon niet worden geladen' : 'Klant niet gevonden'}
        description={error ? getDisplayError(error) : 'De gevraagde klant werd niet gevonden.'}
        onRetry={error ? refreshData : undefined}
        variant="page"
      />
    );
  } else {
    return (
      <>
        <PageHeader title={customer.name} />

        <CustomerProvider customer={customer} refreshData={refreshData}>
          <PageHeading
            leftSide={
              <Breadcrumb
                parentItem={{
                  name: 'Klanten',
                  to: '/internal/customers',
                }}
                currentItem={customer.name}
              />
            }
            rightSide={
              <div className="flex gap-2">
                {customer.deactivationReason ? (
                  <ConfirmDialog
                    triggerText="Heractiveer klant"
                    title="Heractiveer klant"
                    submitText="Heractiveer"
                    description="Ben je zeker dat je de klant wilt heractiveren?"
                    onSubmit={async () => {
                      try {
                        await updateCustomerFetcher(customer.id, {
                          deactivationReason: null,
                        });
                        await refreshData();
                        toast.success('Klant is geheractiveerd');
                      } catch (err) {
                        captureException(err);
                        toast.error('Klant heractiveren mislukt: ' + getDisplayError(err));
                      }
                    }}
                  />
                ) : (
                  <InputDialog
                    triggerText="Deactiveer klant"
                    title="Deactiveer klant"
                    submitText="Deactiveer"
                    description="Ben je zeker dat je de klant wilt deactiveren?"
                    labelText="Reden"
                    validation={Yup.string().min(5, 'Minimum 5 karakters')}
                    onSubmit={async (value: string) => {
                      try {
                        await updateCustomerFetcher(customer.id, {
                          deactivationReason: value,
                        });
                        await refreshData();
                        toast.success('Klant is gedeactiveerd');
                      } catch (err) {
                        captureException(err);
                        toast.error('Klant deactiveren mislukt: ' + getDisplayError(err));
                        throw err;
                      }
                    }}
                  />
                )}
                <Button color="secondary" onTrigger={refreshData}>
                  <ArrowClockwiseIcon className="button-icon" />
                </Button>
              </div>
            }
          />

          <div className="px-4">
            <div className="flex justify-between items-center my-4">
              {!!customer.deactivationReason && (
                <div className="w-full flex mt-2">
                  <DataField title="Deactivatie reden">{customer.deactivationReason}</DataField>
                </div>
              )}

              <div className="flex gap-2 self-end">
                <FileUploaderButton
                  buttonText="Klantenfiche"
                  title="Klantenfiche"
                  initialName={'Klantenfiche'}
                  file={customer.documents.find((d) => d.type === CustomerDocumentType.CustomerFile)?.document}
                  onSubmit={async (document) => {
                    await linkCustomerDocument(customer.id, document.id, CustomerDocumentType.CustomerFile);
                    await refreshData();
                  }}
                />

                <FileUploaderButton
                  buttonText="Akkoord e-facturatie"
                  title="Akkoord e-facturatie"
                  initialName={'Akkoord e-facturatie'}
                  file={customer.documents.find((d) => d.type === CustomerDocumentType.EInvoiceAgreement)?.document}
                  onSubmit={async (document) => {
                    await linkCustomerDocument(customer.id, document.id, CustomerDocumentType.EInvoiceAgreement);
                    await refreshData();
                  }}
                />

                <FileUploaderButton
                  buttonText="Transport Instructies"
                  title="Transport Instructies"
                  initialName={'Transport Instructions'}
                  file={customer.documents.find((d) => d.type === CustomerDocumentType.TransportInstructions)?.document}
                  onSubmit={async (document) => {
                    await linkCustomerDocument(customer.id, document.id, CustomerDocumentType.TransportInstructions);
                    await refreshData();
                  }}
                />

                <MultiFileUploaderButton
                  buttonText="Andere"
                  title="Andere"
                  files={customer.documents.filter((d) => d.type === CustomerDocumentType.Other).map((f) => f.document)}
                  onSubmit={async (documents) => {
                    let count = 0;
                    await Promise.allSettled(
                      documents.map(async (doc) => {
                        try {
                          await linkCustomerDocument(customer.id, doc.id, CustomerDocumentType.Other);
                          count += 1;
                          toast.success(`Bestand: ${doc.name}, ${count}/${documents.length} opgeladen`);
                        } catch (err) {
                          captureException(err);
                          toast.error(`Kon bestand ${doc.name} niet opladen: ${getDisplayError(err)}`);
                        }
                      }),
                    );
                    await refreshData();
                  }}
                  unlinkDocument={async (docId: string) => {
                    try {
                      await unlinkCustomerDocument(customer.id, docId, CustomerDocumentType.Other);
                      await refreshData();
                      toast.success('Bestand gewist');
                    } catch (err) {
                      captureException(err);
                      toast.error(`Kon bestand niet wissen: ${getDisplayError(err)}`);
                    }
                  }}
                />
              </div>
            </div>

            <Tabs items={tabItems} />
          </div>
        </CustomerProvider>
      </>
    );
  }
};

export default CustomerPage;
